<template>
  <rh-etapa-card
    titulo="Contratos e Vínculos"
    subtitulo="Matrícula, cargos, lotação e jornada"
    icone="mdi-briefcase"
  >
    <template #acoes>
      <v-btn
        color="primary"
        outlined
        small
        class="rounded-lg"
        :disabled="removendo || !podeAdicionarMaisProfissionais"
        @click="adicionarRelacaoProfissional"
      >
        <v-icon left>mdi-plus</v-icon> Adicionar Contrato
      </v-btn>
    </template>

    <div
      v-if="!form.profissionais || form.profissionais.length === 0"
      class="text-center py-6 grey--text font-italic"
    >
      Nenhum vínculo profissional cadastrado. Clique no botão acima para adicionar.
    </div>

    <v-card
      v-for="(profissional, index) in form.profissionais"
      :key="'prof-' + index"
      class="rh-subcard mb-4 pa-4"
      outlined
      rounded="lg"
    >
      <div class="d-flex justify-space-between align-center mb-3">
        <span class="text-subtitle-1 font-weight-bold primary--text">
          Vínculo/Contrato #{{ index + 1 }}
        </span>
        <v-btn
          color="error"
          text
          small
          :disabled="removendo"
          aria-label="Remover contrato"
          @click="removerRelacaoProfissional(index)"
        >
          <v-icon :left="$vuetify.breakpoint.smAndUp">mdi-delete-outline</v-icon>
          <span class="d-none d-sm-inline">Remover Contrato</span>
        </v-btn>
      </div>

      <v-row>
        <v-col cols="12" md="8" class="pt-0 pb-0">
          <e-label style="font-weight: bold">Matrícula / ID Funcional</e-label>
          <v-text-field
            v-model="profissional.matricula"
            dense
            solo
            v-mask="'#######################'"
          />
        </v-col>

        <v-col cols="12" md="4" class="pt-0 pb-0">
          <e-label>Regime de Contratação</e-label>
          <v-autocomplete
            :items="$constants.regimeTrabalhistaList"
            :return-object="false"
            v-model="profissional.regime_trabalhista"
            label="Selecione uma opção"
            dense
            solo
          />
        </v-col>

        <v-col cols="12" md="4" class="pt-0 pb-0">
          <div class="rh-link-cargo" @click="abrirDialogNovoCargo('concurso', index)">
            <v-icon x-small color="primary" class="mr-1">mdi-plus-circle-outline</v-icon>
            Adicionar novo cargo
          </div>
          <e-label>Cargo Principal / Contratação</e-label>
          <v-autocomplete
            :items="cargosOptions"
            :return-object="false"
            v-model="profissional.cargo_concurso"
            label="Selecione uma opção"
            dense
            solo
          />
        </v-col>

        <v-col cols="12" md="4" class="pt-0 pb-0">
          <div class="rh-link-cargo" @click="abrirDialogNovoCargo('exercicio', index)">
            <v-icon x-small color="primary" class="mr-1">mdi-plus-circle-outline</v-icon>
            Adicionar novo cargo
          </div>
          <e-label>Cargo em Exercício / Função</e-label>
          <v-autocomplete
            :items="cargosOptions"
            :return-object="false"
            v-model="profissional.cargo_exercicio"
            label="Selecione uma opção"
            dense
            solo
          />
        </v-col>

        <v-col cols="12" md="4" class="pt-0 pb-0" v-if="isSuperAdmin">
          <div class="text-caption mt-1">&nbsp;</div>
          <e-label>Lotação (Filial / Unidade)</e-label>
          <v-autocomplete
            :items="franquiasList"
            :return-object="false"
            v-model="profissional.franquias_permitidas"
            :item-value="(franquia) => franquia.id"
            :item-text="(franquia) => franquia.descricao"
            :loading="carregandoFranquias"
            label="Selecione uma opção"
            dense
            multiple
            solo
          />
        </v-col>

        <v-col cols="12" md="4" class="pt-0 pb-0">
          <e-label>Data de Admissão</e-label>
          <e-date-customize dense solo v-model="profissional.data_ingresso" />
        </v-col>

        <v-col cols="12" md="4" class="pt-0 pb-0">
          <e-label>Carga Horária Semanal</e-label>
          <v-text-field v-model="profissional.ch_total" dense solo v-mask="'#####'" />
        </v-col>

        <v-col cols="12" md="4" class="pt-0 pb-0">
          <e-label>Turno de Trabalho</e-label>
          <v-autocomplete
            :items="$constants.turnosList"
            :return-object="false"
            v-model="profissional.turno"
            label="Selecione uma opção"
            dense
            multiple
            solo
          />
        </v-col>
      </v-row>
    </v-card>

    <rh-documentos-secao
      categoria="contratos"
      titulo="Documentos contratuais"
      subtitulo="Ex.: contrato de trabalho, termo de posse, portaria de nomeação."
      :itens="documentos"
      v-on="$listeners"
    />

    <v-dialog v-model="dialogNovoCargo" max-width="420px" persistent>
      <v-card class="rounded-lg">
        <v-card-title
          class="primary white--text py-3 px-4 d-flex align-center justify-space-between mb-2"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-briefcase-plus-outline</v-icon>
            <span class="text-h6 font-weight-bold">Cadastrar Novo Cargo</span>
          </div>
          <v-btn icon x-small color="white" @click="dialogNovoCargo = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-2">
          <e-label>Nome do Cargo</e-label>
          <v-text-field
            v-model="novoCargo"
            dense
            solo
            autofocus
            placeholder="Ex: Técnico em Enfermagem, Agente Administrativo..."
            @keyup.enter="salvarNovoCargo"
          />
          <div class="text-caption grey--text">
            O novo cargo ficará disponível em "Cargo Principal / Contratação" e "Cargo em Exercício
            / Função" para todos os colaboradores.
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn color="secondary" text @click="dialogNovoCargo = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="salvandoCargo"
            :disabled="!novoCargo.trim()"
            @click="salvarNovoCargo"
          >
            <v-icon left>mdi-check</v-icon> Salvar Cargo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </rh-etapa-card>
</template>

<script>
import RhEtapaCard from "../components/RhEtapaCard.vue";
import RhDocumentosSecao from "../components/RhDocumentosSecao.vue";
import { novoProfissional } from "../config/rhConfig";

export default {
  name: "EtapaContratosVinculos",
  components: { RhEtapaCard, RhDocumentosSecao },
  props: {
    form: { type: Object, required: true },
    documentos: { type: Array, default: () => [] },
    cargosDb: { type: Array, default: () => [] },
    franquiasList: { type: Array, default: () => [] },
    carregandoFranquias: { type: Boolean, default: false },
    isSuperAdmin: { type: Boolean, default: false },
  },
  data() {
    return {
      removendo: false,
      dialogNovoCargo: false,
      novoCargo: "",
      salvandoCargo: false,
      cargoAlvo: null,
      cargoIndice: 0,
    };
  },
  computed: {
    cargosOptions() {
      const doBanco = this.cargosDb.map((c) => c.nome);
      return Array.from(new Set([...(this.$constants.rolesParaRhList || []), ...doBanco]));
    },
    podeAdicionarMaisProfissionais() {
      const lista = this.form.profissionais;
      if (!Array.isArray(lista) || lista.length === 0) {
        return true;
      }
      return Boolean(lista[lista.length - 1].matricula);
    },
  },
  methods: {
    adicionarRelacaoProfissional() {
      this.form.profissionais.push(novoProfissional());
    },
    async removerRelacaoProfissional(index) {
      const profissional = this.form.profissionais[index];

      if (this.form.profissionais.length < 2) {
        this.$toast.danger("É necessário cadastrar pelo menos uma relação profissional");
        return;
      }

      if (profissional.id) {
        try {
          this.removendo = true;
          await this.$services.recursosHumanosService.removerProfissional(profissional.id);
          this.$toast.success("Relação profissional removida com sucesso");
        } catch (error) {
          this.$handleError(error);
          return;
        } finally {
          this.removendo = false;
        }
      }

      this.form.profissionais.splice(index, 1);
    },
    abrirDialogNovoCargo(tipo, index) {
      this.cargoAlvo = tipo;
      this.cargoIndice = index;
      this.novoCargo = "";
      this.dialogNovoCargo = true;
    },
    async salvarNovoCargo() {
      const nomeCargo = String(this.novoCargo || "").trim();
      if (!nomeCargo) {
        return;
      }

      this.salvandoCargo = true;
      try {
        const cargo = await this.$services.recursosHumanosService.criarCargo(nomeCargo);
        this.$emit("cargo-criado", cargo);

        const campo = this.cargoAlvo === "concurso" ? "cargo_concurso" : "cargo_exercicio";
        this.$set(this.form.profissionais[this.cargoIndice], campo, cargo.nome);

        this.$toast.success(`Cargo "${cargo.nome}" cadastrado com sucesso`);
        this.dialogNovoCargo = false;
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvandoCargo = false;
      }
    },
  },
};
</script>

<style scoped>
.rh-subcard {
  background-color: var(--surface-2) !important;
  border: 1px dashed var(--border) !important;
}

.rh-link-cargo {
  cursor: pointer;
  color: var(--brand);
  font-size: 0.75rem;
  font-weight: 500;
  margin-top: 4px;
}
</style>
